import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'storfox-snackbar'
import { useAllSearchParams } from 'storfox-route-hooks'
import { prop } from 'ramda'
import { useDeepCompareEffect } from 'storfox-api-hooks'

import { escapeAtob, unescapeBtoa } from '~/utils'
import { DashboardLayout } from '~/components/Layouts'
import { useNotification } from '~/components/Notification'
import * as NAV from '~/constants/nav-titles'
import * as ROUTES from '~/constants/routes'
import {
  ADJUSTMENT_BULK_CREATE_COMPLETE,
  ADJUSTMENT_BULK_CREATE_STATUS,
  ADJUSTMENT_BULK_VALIDATE_COMPLETE,
  ADJUSTMENT_BULK_VALIDATE_STATUS
} from '~/constants/notification-topics'
import { useProgressCard } from '~/components/ProgressCard'
import { useGoogleEvent } from '~/components/GoogleAnalytics/GoogleAnalytics'
import useMessages from '~/hooks/useMessages'

import AdjustmentImportForm from '../components/AdjustmentImport'
import { useAdjustmentImport } from '../hooks'

function AdjustmentImportContainer () {
  const adjustmentImport = useAdjustmentImport()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const messages = useMessages()
  const { payload } = useAllSearchParams()
  const { sendEvent } = useGoogleEvent()
  const progressCard = useProgressCard()

  const validateComplete = useNotification(ADJUSTMENT_BULK_VALIDATE_COMPLETE)
  const uploadComplete = useNotification(ADJUSTMENT_BULK_CREATE_COMPLETE)
  const validateStatus = useNotification(ADJUSTMENT_BULK_VALIDATE_STATUS)
  const uploadStatus = useNotification(ADJUSTMENT_BULK_CREATE_STATUS)

  const key = prop('key', escapeAtob(payload))

  useDeepCompareEffect(() => {
    if (key) {
      validateStatus.subscribe(key, ({ payload }) => {
        const result = prop('result', payload)
        const validated = prop('totalValidated', result)
        const total = prop('total', result)

        progressCard.handleValidationChange({ current: validated, total })
      })

      validateComplete.subscribe(key, ({ payload }) => {
        navigate(
          `${ROUTES.ADJUSTMENT_IMPORT_PATH}?payload=${unescapeBtoa(payload)}`
          , { replace: true })

        validateComplete.setLoading(false)
      })

      uploadStatus.subscribe(key, ({ payload }) => {
        const result = prop('result', payload)
        const completed = prop('totalCreated', result)
        const total = prop('total', result)
        if (!uploadComplete.isLoading) {
          uploadComplete.setLoading(true)
          progressCard.handleValidationChange({ current: total, total })
        }
        progressCard.handleCompletionChange({ current: completed, total })
      })

      uploadComplete.subscribe(key, () => {
        navigate(ROUTES.ADJUSTMENT_LIST_PATH, { replace: true })
        uploadComplete.setLoading(false)
        sendEvent({ eventAction: 'Upload File Adjustment', eventCategory: 'Adjustment' })
      })
    }

    return () => {
      validateStatus.unsubscribe()
      validateComplete.unsubscribe()
      uploadStatus.unsubscribe()
      uploadComplete.unsubscribe()
    }
  }, [key])

  const handleSubmit = () => {
    uploadComplete.setLoading(true)
    return adjustmentImport.create({ key })
      .then(() => snackbar({ message: messages.getUploadNotification('adjustment') }))
  }

  const handleSubscribe = key => {
    snackbar({ message: messages.NOTIFICATION_WAIT })
    validateComplete.setLoading(true)
    navigate(`${ROUTES.ADJUSTMENT_IMPORT_PATH}?payload=${unescapeBtoa({ key })}`, { replace: true })
    sendEvent({ eventAction: 'Validate File Adjustment', eventCategory: 'Adjustment' })
  }

  const handleFileRemove = () => {
    validateComplete.unsubscribe()
    uploadComplete.unsubscribe()
    validateStatus.unsubscribe()
    uploadStatus.unsubscribe()
    progressCard.resetValues()
  }

  const title = 'Import adjustments'

  const isLoading = (
    adjustmentImport.isLoading ||
    validateComplete.isLoading ||
    uploadComplete.isLoading
  )

  const breadcrumbs = { title }

  return (
    <DashboardLayout
      title={title}
      activeNav={NAV.ADJUSTMENT}
      isLoading={validateComplete.isLoading || uploadComplete.isLoading}
      breadcrumbs={breadcrumbs}
    >
      <AdjustmentImportForm
        title={title}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        progressCard={progressCard}
        onFileRemove={handleFileRemove}
        onSubscribe={handleSubscribe}
      />
    </DashboardLayout>
  )
}

export default AdjustmentImportContainer
